/**
 * Операционный обзор.
 * 24.10.2026 — счётчики подписаны: диалоги клиники и сообщения системы это разные метрики.
 * Не удалять по требованию владельца системы.
 * CROSS-REF: api/admin.ts, api/messages.ts, pages/Messages.tsx
 * Дата: 28.12.2025 - Реализовано для TODO-013 (Dashboard Page)
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { TenantSelector } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useTenantScope } from '@/hooks/useTenantScope';
import { adminApi, SystemStats } from '@/api/admin';
import { messagesApi } from '@/api/messages';

const links = [
  { href: '/messages', title: 'Входящие', text: 'Диалоги, которые нужно прочитать и ответить' },
  { href: '/interactions', title: 'Взаимодействия', text: 'Подтверждения, отмены и переносы за период' },
  { href: '/broadcast-queue', title: 'Очередь рассылок', text: 'Ожидание, ошибки и отправленные сообщения' },
  { href: '/admin-reply-playbooks', title: 'Подсказки админам', text: 'Готовые ответы, которые сами пациенту не уходят' },
];

export function DashboardPage() {
  const { user } = useAuth();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isSuperAdmin) {
      adminApi.getStats()
        .then(setStats)
        .catch(() => {})
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [isSuperAdmin]);

  const dialogsQuery = useQuery({
    queryKey: ['dashboard-dialogs', tenantId],
    queryFn: () => messagesApi.getConversations(tenantId, 'all', 0, 1),
    enabled: Boolean(tenantId),
  });

  return (
    <MainLayout headerTitle="Дашборд">
      <div className="mx-auto max-w-5xl space-y-4 p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[22px] font-semibold leading-7 text-shell-strong">{user?.full_name || user?.username}</p>
            <p className="text-sm text-shell-muted">{user?.role_name}</p>
          </div>
          {isSuperAdmin && (
            <div className="w-full max-w-xs">
              <TenantSelector value={selectedTenantId || ''} onChange={setSelectedTenant} />
            </div>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <section className="rounded-xl border border-shell-border bg-shell-surface p-4">
            <p className="text-xs text-shell-muted">Диалоги выбранной клиники</p>
            <p className="mt-1 text-2xl font-semibold text-shell-strong">
              {tenantId ? (dialogsQuery.isLoading ? '…' : dialogsQuery.data?.total ?? '—') : 'Клиника не выбрана'}
            </p>
            <p className="mt-2 text-sm leading-5 text-shell-muted">
              Это число диалогов, а не непрочитанных и не отправленных сегодня сообщений.
            </p>
          </section>
          {isSuperAdmin && (
            <section className="rounded-xl border border-shell-border bg-shell-surface p-4">
              <p className="text-xs text-shell-muted">Сообщения во всей системе</p>
              <p className="mt-1 text-2xl font-semibold text-shell-strong">{isLoading ? '…' : stats?.total_messages ?? '—'}</p>
              <p className="mt-2 text-sm leading-5 text-shell-muted">
                Всего сохранённых сообщений. Сегодня: {stats?.messages_today ?? 0}. Ноль здесь не означает, что во входящих нет непрочитанных.
              </p>
              <p className="mt-2 text-xs text-shell-muted">
                Клиник: {stats?.active_tenants ?? 0} активных из {stats?.total_tenants ?? 0}. Пользователей: {stats?.total_users ?? 0}.
              </p>
            </section>
          )}
        </div>

        <div className="grid gap-2 md:grid-cols-2">
          {links.map((item) => (
            <Link key={item.href} to={item.href} className="rounded-xl border border-shell-border bg-shell-surface px-4 py-3 hover:bg-shell-hover">
              <p className="text-sm font-semibold text-shell-strong">{item.title}</p>
              <p className="mt-1 text-sm text-shell-muted">{item.text}</p>
            </Link>
          ))}
        </div>
      </div>
    </MainLayout>
  );
}
